/**
 * ============================================================
 * IZAPA — Gift with Purchase (GWP)
 * ============================================================
 * JS puro, sin dependencias. Usa la Cart AJAX API de Shopify
 * (/cart.js, /cart/add.js, /cart/change.js) para:
 *
 *   1. Mostrar un selector de regalo cuando el carrito califica
 *      (sin contar el regalo mismo). Las reglas se configuran en
 *      snippets/gift-with-purchase.liquid:
 *        - data-min-qty: mínimo de productos (1 = cualquier compra)
 *        - data-threshold: monto mínimo en centavos (0 = desactivado)
 *      Califica si cumple CUALQUIERA de las dos (y hay al menos 1 producto).
 *   2. Mostrar una VENTANA EMERGENTE (modal) automáticamente en el
 *      momento exacto en que el carrito califica sin regalo elegido.
 *   3. Si la persona cierra la ventana sin elegir, dejar visible el
 *      selector dentro del carrito (drawer y/o página de carrito).
 *   4. Dejar que la persona elija UNA de las dos lociones de 30 ml.
 *   5. Quitar el regalo automáticamente si el carrito deja de calificar
 *      (por ejemplo, al vaciarlo).
 *   6. Garantizar máximo 1 regalo por pedido.
 * ============================================================
 */
(function () {
  'use strict';

  const WIDGET_SELECTOR = '#gwp-widget';
  const PROGRESS_SELECTOR = '#gwp-progress';
  const GIFT_PROPERTY_KEY = '_gift'; // El prefijo "_" oculta la propiedad al cliente
  const MODAL_FLAG_KEY = 'gwp_needs_gift'; // sessionStorage: evita repetir la ventana

  let widgets = [];
  let progressEls = [];
  let modalOverlay = null;
  let isSyncing = false;
  let pendingResync = false;

  function init() {
    widgets = Array.from(document.querySelectorAll(WIDGET_SELECTOR));
    if (!widgets.length) return;
    progressEls = Array.from(document.querySelectorAll(PROGRESS_SELECTOR));

    bindPickerEvents();
    buildModal();
    patchFetchForCartSync();
    bindKnownThemeEvents();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', syncFromCart);
    } else {
      syncFromCart();
    }
    window.addEventListener('pageshow', syncFromCart);
  }

  function config() {
    const widget = widgets[0];
    return {
      thresholdCents: parseInt(widget.dataset.threshold, 10) || 0,
      minQty: parseInt(widget.dataset.minQty, 10) || 0,
      variantA: widget.dataset.giftVariantA,
      variantB: widget.dataset.giftVariantB,
      refreshSections: (widget.dataset.refreshSections || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    };
  }

  /* ------------------------------------------------------------ *
   * 1) Sincronización con el carrito
   * ------------------------------------------------------------ */

  function patchFetchForCartSync() {
    const originalFetch = window.fetch;
    window.fetch = function (...args) {
      const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
      const isCartMutation = /\/cart\/(add|change|update|clear)(\.js)?/.test(url);
      const result = originalFetch.apply(this, args);
      if (isCartMutation) {
        result
          .then(() => {
            setTimeout(syncFromCart, 50);
          })
          .catch(() => {});
      }
      return result;
    };
  }

  function bindKnownThemeEvents() {
    ['cart:updated', 'cart:change', 'theme:cart:change'].forEach((eventName) => {
      document.addEventListener(eventName, syncFromCart);
    });
  }

  async function syncFromCart() {
    try {
      const cart = await fetchCart();
      await applyRules(cart);
    } catch (err) {
      console.error('[GWP] No se pudo sincronizar el carrito:', err);
    }
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
  }

  function isGiftItem(item) {
    return !!(item.properties && item.properties[GIFT_PROPERTY_KEY] === 'true');
  }

  function findGiftLine(cart) {
    return cart.items.find(isGiftItem);
  }

  function nonGiftSubtotalCents(cart) {
    return cart.items.reduce((sum, item) => (isGiftItem(item) ? sum : sum + item.line_price), 0);
  }

  // Unidades en el carrito sin contar la línea de regalo.
  function nonGiftQuantity(cart) {
    return cart.items.reduce((sum, item) => (isGiftItem(item) ? sum : sum + item.quantity), 0);
  }

  // Califica si hay al menos 1 producto (no regalo) Y se cumple la cantidad
  // mínima o el monto mínimo configurados. Con min-qty = 1 es "cualquier compra".
  function cartQualifies(cart) {
    const { thresholdCents, minQty } = config();
    const qty = nonGiftQuantity(cart);
    if (qty < 1) return false;
    const subtotal = nonGiftSubtotalCents(cart);
    return (minQty > 0 && qty >= minQty) || (thresholdCents > 0 && subtotal >= thresholdCents);
  }

  /* ------------------------------------------------------------ *
   * 2) Reglas de negocio
   * ------------------------------------------------------------ */

  async function applyRules(cart) {
    if (isSyncing) {
      pendingResync = true;
      return;
    }

    const giftLine = findGiftLine(cart);
    const qualifies = cartQualifies(cart);

    // Si el carrito deja de calificar (p. ej. se vació), quita el regalo.
    if (!qualifies && giftLine) {
      isSyncing = true;
      try {
        await changeLine(giftLine.key, 0);
        cart = await fetchCart();
      } finally {
        isSyncing = false;
      }
    }

    // Máximo 1 línea de regalo.
    const giftLines = cart.items.filter(isGiftItem);
    if (giftLines.length > 1) {
      isSyncing = true;
      try {
        for (let i = 1; i < giftLines.length; i++) {
          await changeLine(giftLines[i].key, 0);
        }
        cart = await fetchCart();
      } finally {
        isSyncing = false;
      }
    }

    // Máximo 1 unidad en la línea de regalo.
    const soloGift = findGiftLine(cart);
    if (soloGift && soloGift.quantity > 1) {
      isSyncing = true;
      try {
        await changeLine(soloGift.key, 1);
        cart = await fetchCart();
      } finally {
        isSyncing = false;
      }
    }

    render(cart, cartQualifies(cart));

    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart } }));
    await maybeRefreshSections();

    if (pendingResync) {
      pendingResync = false;
      syncFromCart();
    }
  }

  /* ------------------------------------------------------------ *
   * 3) Renderizado
   * ------------------------------------------------------------ */

  function render(cart, qualifies) {
    const giftLine = findGiftLine(cart);
    const { thresholdCents, minQty } = config();

    // El mensaje de progreso solo aplica si se exige más de 1 producto o un monto.
    const progressActive = !qualifies && (minQty > 1 || thresholdCents > 0);

    progressEls.forEach((progressEl) => {
      progressEl.hidden = !progressActive;
      if (!progressActive) return;

      const subtotal = nonGiftSubtotalCents(cart);
      const qty = nonGiftQuantity(cart);
      const moneyRemaining = Math.max(thresholdCents - subtotal, 0);
      const itemsRemaining = Math.max(minQty - qty, 0);
      const itemWord = itemsRemaining === 1 ? 'producto' : 'productos';

      const textEl = progressEl.querySelector('[data-gwp-progress-text]');
      if (textEl) {
        if (minQty > 1 && thresholdCents > 0) {
          textEl.textContent =
            'Agrega ' + itemsRemaining + ' ' + itemWord + ' más, o compra ' +
            formatMoney(moneyRemaining, cart.currency) + ' más, y elige un regalo de cortesía.';
        } else if (minQty > 1) {
          textEl.textContent =
            'Agrega ' + itemsRemaining + ' ' + itemWord + ' más y elige un regalo de cortesía.';
        } else {
          textEl.textContent =
            'Agrega ' + formatMoney(moneyRemaining, cart.currency) + ' más y elige un regalo de cortesía.';
        }
      }

      const fillEl = progressEl.querySelector('[data-gwp-fill]');
      if (fillEl) {
        const qtyPct = minQty > 1 ? (qty / minQty) * 100 : 0;
        const moneyPct = thresholdCents > 0 ? (subtotal / thresholdCents) * 100 : 0;
        fillEl.style.width = Math.min(100, Math.round(Math.max(qtyPct, moneyPct))) + '%';
      }
    });

    widgets.forEach((widgetEl) => {
      widgetEl.hidden = !qualifies;
    });

    if (qualifies) {
      widgets.forEach((widgetEl) => {
        const pickerEl = widgetEl.querySelector('[data-gwp-state="picker"]');
        const selectedEl = widgetEl.querySelector('[data-gwp-state="selected"]');

        if (giftLine) {
          pickerEl.hidden = true;
          selectedEl.hidden = false;
          const nameEl = selectedEl.querySelector('[data-gwp-selected-name]');
          const imgEl = selectedEl.querySelector('[data-gwp-selected-image]');
          if (nameEl) nameEl.textContent = giftLine.product_title || giftLine.title;
          if (imgEl && giftLine.image) {
            imgEl.src = giftLine.image;
            imgEl.alt = giftLine.product_title || giftLine.title;
          }
        } else {
          pickerEl.hidden = false;
          selectedEl.hidden = true;
        }
      });

      document.querySelectorAll('[data-gwp-select]').forEach((btn) => {
        btn.classList.toggle(
          'is-selected',
          !!giftLine && String(btn.dataset.variantId) === String(giftLine.variant_id || giftLine.id)
        );
      });
    }

    maybeShowModal(qualifies, giftLine);
  }

  function formatMoney(cents, currency) {
    try {
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: currency || 'MXN',
      }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  /* ------------------------------------------------------------ *
   * 4) Acciones del usuario
   * ------------------------------------------------------------ */

  function bindPickerEvents() {
    widgets.forEach((widgetEl) => {
      widgetEl.addEventListener('click', async (e) => {
        const selectBtn = e.target.closest('[data-gwp-select]');
        const changeBtn = e.target.closest('[data-gwp-change]');

        if (selectBtn) {
          e.preventDefault();
          await selectGift(selectBtn.dataset.variantId);
        }

        if (changeBtn) {
          e.preventDefault();
          widgetEl.querySelector('[data-gwp-state="picker"]').hidden = false;
          widgetEl.querySelector('[data-gwp-state="selected"]').hidden = true;
        }
      });
    });
  }

  async function selectGift(variantId) {
    setStatus('Agregando tu regalo…');
    isSyncing = true;
    try {
      const cart = await fetchCart();
      const existingGift = findGiftLine(cart);

      if (existingGift && String(existingGift.variant_id) !== String(variantId)) {
        await changeLine(existingGift.key, 0);
      }
      if (!existingGift || String(existingGift.variant_id) !== String(variantId)) {
        await addGift(variantId);
      }
      setStatus('¡Regalo agregado a tu carrito!');
      closeModal();
    } catch (err) {
      console.error('[GWP] Error al seleccionar el regalo:', err);
      setStatus('No pudimos agregar el regalo. Intenta de nuevo.');
    } finally {
      isSyncing = false;
      syncFromCart();
    }
  }

  function addGift(variantId) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1, properties: { [GIFT_PROPERTY_KEY]: 'true' } }],
      }),
    }).then((r) => {
      if (!r.ok) throw new Error('add.js failed');
      return r.json();
    });
  }

  function changeLine(lineKey, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: quantity }),
    }).then((r) => {
      if (!r.ok) throw new Error('change.js failed');
      return r.json();
    });
  }

  function setStatus(msg) {
    document.querySelectorAll('[data-gwp-status]').forEach((el) => {
      el.textContent = msg;
    });
  }

  /* ------------------------------------------------------------ *
   * 5) Ventana emergente (modal)
   * ------------------------------------------------------------ */

  function buildModal() {
    const optionsSource = widgets[0] && widgets[0].querySelector('.gwp__options');
    if (!optionsSource) return;

    const overlay = document.createElement('div');
    overlay.className = 'gwp-modal-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'gwp-modal-title');
    overlay.hidden = true;

    const dialog = document.createElement('div');
    dialog.className = 'gwp-modal';

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'gwp-modal__close';
    closeBtn.setAttribute('aria-label', 'Cerrar');
    closeBtn.innerHTML = '&times;';

    const header = document.createElement('div');
    header.className = 'gwp-modal__header';
    header.innerHTML =
      '<p class="gwp__eyebrow">Regalo por tu compra</p>' +
      '<h3 class="gwp__title" id="gwp-modal-title">¡Tienes un regalo! Elige el tuyo</h3>' +
      '<p class="gwp__subtitle">Por tu compra en IZAPA te regalamos una loción para manos y cuerpo de 30&nbsp;ml. Elige tu aroma:</p>';

    const optionsClone = optionsSource.cloneNode(true);
    optionsClone.classList.add('gwp-modal__options');

    const status = document.createElement('p');
    status.className = 'gwp__status gwp-modal__status';
    status.setAttribute('data-gwp-status', '');
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');

    const dismiss = document.createElement('button');
    dismiss.type = 'button';
    dismiss.className = 'gwp-modal__dismiss';
    dismiss.textContent = 'Elegir más tarde';

    dialog.appendChild(closeBtn);
    dialog.appendChild(header);
    dialog.appendChild(optionsClone);
    dialog.appendChild(status);
    dialog.appendChild(dismiss);
    overlay.appendChild(dialog);
    document.body.appendChild(overlay);

    closeBtn.addEventListener('click', closeModal);
    dismiss.addEventListener('click', closeModal);
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) closeModal();
    });
    dialog.addEventListener('click', async (e) => {
      const selectBtn = e.target.closest('[data-gwp-select]');
      if (selectBtn) {
        e.preventDefault();
        await selectGift(selectBtn.dataset.variantId);
      }
    });

    modalOverlay = overlay;
  }

  function onModalKeydown(e) {
    if (e.key === 'Escape') closeModal();
  }

  function openModal() {
    if (!modalOverlay || !modalOverlay.hidden) return;
    modalOverlay.hidden = false;
    // eslint-disable-next-line no-unused-expressions
    modalOverlay.offsetHeight; // forzar reflow para la transición
    modalOverlay.classList.add('is-open');
    document.documentElement.classList.add('gwp-modal-open');
    document.addEventListener('keydown', onModalKeydown);
    const closeBtn = modalOverlay.querySelector('.gwp-modal__close');
    if (closeBtn) closeBtn.focus();
  }

  function closeModal() {
    if (!modalOverlay || modalOverlay.hidden) return;
    modalOverlay.classList.remove('is-open');
    document.documentElement.classList.remove('gwp-modal-open');
    document.removeEventListener('keydown', onModalKeydown);
    setTimeout(() => {
      if (modalOverlay && !modalOverlay.classList.contains('is-open')) {
        modalOverlay.hidden = true;
      }
    }, 220);
  }

  // Abre la ventana solo cuando el carrito pasa a "califica y no tiene regalo".
  // Si la persona la cierra sin elegir, no se reabre sola; queda el selector
  // dentro del carrito como recordatorio.
  function maybeShowModal(qualifies, giftLine) {
    const needsGift = !!qualifies && !giftLine;
    let prevNeedsGift = false;
    try {
      prevNeedsGift = sessionStorage.getItem(MODAL_FLAG_KEY) === '1';
    } catch (e) {}

    if (needsGift && !prevNeedsGift) {
      openModal();
    } else if (!needsGift) {
      closeModal();
    }

    try {
      sessionStorage.setItem(MODAL_FLAG_KEY, needsGift ? '1' : '0');
    } catch (e) {}
  }

  /* ------------------------------------------------------------ *
   * 6) Refrescar secciones del tema (Section Rendering API)
   * ------------------------------------------------------------ */

  async function maybeRefreshSections() {
    const { refreshSections } = config();
    if (!refreshSections.length) return;

    try {
      const params = new URLSearchParams({ sections: refreshSections.join(',') });
      const res = await fetch('/cart?' + params.toString(), {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return;
      const data = await res.json();
      Object.keys(data).forEach((sectionId) => {
        const container = document.getElementById('shopify-section-' + sectionId);
        if (!container) return;
        const parsed = new DOMParser().parseFromString(data[sectionId], 'text/html');
        const newInner = parsed.getElementById('shopify-section-' + sectionId);
        container.innerHTML = newInner ? newInner.innerHTML : parsed.body.innerHTML;
      });
    } catch (err) {
      console.warn('[GWP] No se pudieron refrescar las secciones del carrito:', err);
    }
  }

  init();
})();
